import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

interface Request {
  id: string;
  type: string;
  status: string;
  created_at: string;
  updated_at?: string;
  user_id: string;
  assigned_to: string | null;
  brand_id: string;
  answers: any;
  questions_and_answers: any;
  assigned_user?: {
    user_metadata: {
      full_name?: string;
    };
    email?: string;
  };
  brands?: {
    name: string;
    description?: string;
  };
  unreadCount?: number;
}

interface KanbanBoardProps {
  requests: Request[];
  onStatusChange: (requestId: string, newStatus: string) => void;
  onAssignRequest: (requestId: string) => void;
}

const KanbanBoard: React.FC<KanbanBoardProps> = ({ requests, onStatusChange, onAssignRequest }) => {
  const { user } = useAuth();
  const [draggedRequest, setDraggedRequest] = useState<string | null>(null);
  const [requestsWithNotifications, setRequestsWithNotifications] = useState<Request[]>([]);

  // Fetch unread message counts for each request
  useEffect(() => {
    const fetchUnreadCounts = async () => {
      if (!requests.length) return;

      try {
        // Get all request IDs
        const requestIds = requests.map(req => req.id);
        
        // Fetch unread message counts for these requests
        const { data: messageCounts, error } = await supabase
          .from('request_message')
          .select('request_id, sender, read')
          .in('request_id', requestIds)
          .eq('sender', 'user') // Only count messages from users (clients)
          .eq('read', false); // Only count unread messages

        if (!error && messageCounts) {
          // Count messages per request
          const unreadCounts = messageCounts.reduce((acc: any, msg) => {
            acc[msg.request_id] = (acc[msg.request_id] || 0) + 1;
            return acc;
          }, {});

          // Add unread counts to requests
          const updatedRequests = requests.map(req => ({
            ...req,
            unreadCount: unreadCounts[req.id] || 0
          }));

          setRequestsWithNotifications(updatedRequests);
        }
      } catch (error) {
        console.error('Error fetching unread counts:', error);
        setRequestsWithNotifications(requests);
      }
    };

    fetchUnreadCounts();
  }, [requests]);

  // Use requestsWithNotifications if available, otherwise use original requests
  const displayRequests = requestsWithNotifications.length > 0 ? requestsWithNotifications : requests;

  // Set up real-time subscription for new messages
  useEffect(() => {
    // Subscribe to new messages to update notification counts
    const messageSubscription = supabase
      .channel('kanban_messages')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'request_message',
        },
        async (payload) => {
          const newMessage = payload.new;
          
          // Only update if it's a message from a user (client) that's unread
          if (newMessage.sender === 'user' && !newMessage.read) {
            console.log('🔔 New unread message received in kanban:', newMessage);
            
            // Update the unread count for this request
            setRequestsWithNotifications(prev => prev.map(req => 
              req.id === newMessage.request_id 
                ? { ...req, unreadCount: (req.unreadCount || 0) + 1 }
                : req
            ));
          }
        }
      )
      .subscribe();

    return () => {
      messageSubscription.unsubscribe();
    };
  }, []);

  const swimLanes = [
    { id: 'pending', title: 'Pending', color: 'bg-yellow-50 border-yellow-200', status: 'pending' },
    { id: 'in-progress', title: 'In Progress', color: 'bg-blue-50 border-yellow-200', status: 'in_progress' },
    { id: 'review', title: 'Review', color: 'bg-purple-50 border-purple-200', status: 'review' },
    { id: 'completed', title: 'Completed', color: 'bg-green-50 border-green-200', status: 'completed' }
  ];

  const handleDragStart = (e: React.DragEvent, requestId: string) => {
    setDraggedRequest(requestId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, targetStatus: string) => {
    e.preventDefault();
    if (draggedRequest) {
      onStatusChange(draggedRequest, targetStatus);
      setDraggedRequest(null);
    }
  };

  const getRequestsByStatus = (status: string) => {
    const filtered = displayRequests.filter(request => request.status === status);
    return filtered;
  };

  const formatRequestType = (type: string) => {
    return type.replace(/-/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase());
  };

  const getTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInHours < 168) return `${Math.floor(diffInHours / 24)}d ago`;
    return `${Math.floor(diffInHours / 168)}w ago`;
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-6">Kanban Board</h2>
      
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {swimLanes.map((lane) => (
          <div
            key={lane.id}
            className={`${lane.color} border-2 rounded-lg p-4 h-[600px] flex flex-col`}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, lane.status)}
          >
            <div className="flex items-center justify-between mb-4 flex-shrink-0">
              <h3 className="font-semibold text-gray-800">{lane.title}</h3>
              <span className="bg-white px-2 py-1 rounded-full text-sm font-medium text-gray-600">
                {getRequestsByStatus(lane.status).length}
              </span>
            </div>
            
            <div className="space-y-3 overflow-y-auto flex-1">
              {getRequestsByStatus(lane.status).map((request) => (
                <div
                  key={request.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, request.id)}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 cursor-move hover:shadow-md transition-shadow"
                >
                  {/* Brand Name */}
                  {request.brands?.name ? (
                    <div className="mb-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2 p-2 bg-blue-50 rounded-lg border border-blue-100">
                          <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                          <span className="text-sm font-semibold text-blue-900">
                            {request.brands.name}
                          </span>
                        </div>
                        {/* Notification Bell for Unread Messages */}
                        {request.unreadCount && request.unreadCount > 0 && (
                          <div className="relative">
                            <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
                              <span className="text-white text-xs font-bold">
                                {request.unreadCount > 9 ? '9+' : request.unreadCount}
                              </span>
                            </div>
                            <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-600 rounded-full animate-pulse"></div>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="mb-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2 p-2 bg-gray-50 rounded-lg border border-gray-100">
                          <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
                          <span className="text-sm font-medium text-gray-600">
                            No Brand
                          </span>
                        </div>
                        {/* Notification Bell for Unread Messages */}
                        {request.unreadCount && request.unreadCount > 0 && (
                          <div className="relative">
                            <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
                              <span className="text-white text-xs font-bold">
                                {request.unreadCount > 9 ? '9+' : request.unreadCount}
                              </span>
                            </div>
                            <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-600 rounded-full animate-pulse"></div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-start justify-between mb-2">
                    <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-1 rounded">
                      {formatRequestType(request.type)}
                    </span>
                    <span className="text-xs text-gray-500">
                      {getTimeAgo(request.created_at)}
                    </span>
                  </div>
                  
                  <div className="mb-3">
                    <p className="text-sm text-gray-600 line-clamp-2">
                      {(() => {
                        if (!request.questions_and_answers) {
                          return 'No details available';
                        }
                        
                        if (typeof request.questions_and_answers === 'string') {
                          return request.questions_and_answers.substring(0, 100) + '...';
                        }
                        
                        if (Array.isArray(request.questions_and_answers)) {
                          // If it's an array, try to extract meaningful text
                          const firstAnswer = request.questions_and_answers.find(qa => 
                            qa && typeof qa === 'object' && 'answer' in qa && typeof qa.answer === 'string'
                          );
                          if (firstAnswer && typeof firstAnswer === 'object' && 'answer' in firstAnswer) {
                            const answer = firstAnswer.answer;
                            if (typeof answer === 'string') {
                              return answer.substring(0, 100) + '...';
                            }
                          }
                          return 'Request details available';
                        }
                        
                        if (typeof request.questions_and_answers === 'object' && request.questions_and_answers !== null) {
                          // If it's an object, try to extract meaningful text
                          const answers = Object.values(request.questions_and_answers);
                          const firstStringAnswer = answers.find(ans => typeof ans === 'string' && ans.length > 0);
                          if (firstStringAnswer && typeof firstStringAnswer === 'string') {
                            return firstStringAnswer.substring(0, 100) + '...';
                          }
                          return 'Request details available';
                        }
                        
                        return 'Request details available';
                      })()}
                    </p>
                  </div>
                  
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                    <span>ID: {request.id.slice(0, 8)}...</span>
                    <div className="flex items-center space-x-2">
                      {request.assigned_to ? (
                        <div className="flex items-center space-x-1">
                          <span className="text-green-600">👤</span>
                          <span className="text-green-600 font-medium">
                            {request.assigned_user?.user_metadata?.full_name || 
                             request.assigned_user?.email?.split('@')[0] || 
                             'Unknown User'}
                          </span>
                        </div>
                      ) : (
                        <span className="text-gray-500">📭 Unassigned</span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex space-x-2">
                    {!request.assigned_to && (
                      <button
                        onClick={() => onAssignRequest(request.id)}
                        className="text-xs bg-primary text-white px-2 py-1 rounded hover:bg-primary/80 transition-colors"
                      >
                        Assign
                      </button>
                    )}
                    <Link
                      to={`/admin/requests/${request.id}`}
                      className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded hover:bg-gray-200 transition-colors"
                    >
                      View
                    </Link>
                  </div>
                </div>
              ))}
              
              {getRequestsByStatus(lane.status).length === 0 && (
                <div className="text-center py-8 text-gray-400">
                  <div className="text-4xl mb-2">📭</div>
                  <p className="text-sm">No requests</p>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      
      <div className="mt-6 text-center text-sm text-gray-500">
        <p>💡 Drag and drop requests between columns to update their status</p>
        <p className="mt-1">📱 Scroll vertically within each column to see all requests</p>
      </div>
    </div>
  );
};

export default KanbanBoard; 